import { createSlice } from "@reduxjs/toolkit";
import { REPORTING } from "modules/reporting/constants";
import moment from "moment";
import {
    getOpeningBalance,
    getAllocatedCredits,
    getCreditsUsed,
    getCreditsReimbursed,
    getClosingBalance,
    getEligibleRollover,
    getTotalPrintJobs,
    getUtilizationRate,
    getMonthlyCreditUsage,
    getCreditsUsedVsReimbursed,
    getPrintedVsRequestedQuantity,
    getQcImpactByMonth,
    getQcFailByProduct,
} from "../action";

// Get current month as default filter
const getCurrentMonthFilter = () => ({
    year: moment().year(),
    month: moment().month(), // 0-indexed (0 = January)
});

const initialState = {
    // Executive Overview metrics
    executiveOverview: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        openingBalance: 0,
        allocatedCredits: 0,
        creditsUsed: 0,
        creditsReimbursed: 0,
        closingBalance: 0,
        eligibleRollover: 0,
        totalPrintJobs: 0,
        utilizationRate: 0,
        openingBalanceChange: 0,
        creditsUsedChange: 0,
        creditsReimbursedChange: 0,
    },
    // Monthly Credit Usage chart data
    monthlyUsageData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        labels: [],
        datasets: [],
    },
    // Credits Used vs Reimbursed chart data
    creditsUsedVsReimbursedData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        labels: [],
        datasets: [],
    },
    // Printed Quantity vs Requested Quantity chart data
    printedVsRequestedData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        labels: [],
        datasets: [],
    },
    // QC Impact by Month chart data
    qcImpactByMonthData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        labels: [],
        datasets: [],
    },
    // QC Fail by Product chart data (Pie Chart)
    qcFailByProductData: {
        isLoading: false,
        isSuccess: false,
        noResult: false,
        pieLabels: [],
        pieTotal: [],
    },
    // Active tab
    activeTab: 0,
    // Period filter (weekly/monthly)
    periodFilter: "monthly",
    // Month filter for Executive Overview
    selectedMonth: getCurrentMonthFilter(),
};

const reportingCreditSlice = createSlice({
    name: REPORTING,
    initialState,
    reducers: {
        initReportingCredit: (state) => {
            state.executiveOverview = initialState.executiveOverview;
            state.monthlyUsageData = initialState.monthlyUsageData;
            state.creditsUsedVsReimbursedData = initialState.creditsUsedVsReimbursedData;
            state.printedVsRequestedData = initialState.printedVsRequestedData;
            state.qcImpactByMonthData = initialState.qcImpactByMonthData;
            state.qcFailByProductData = initialState.qcFailByProductData;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        setPeriodFilter: (state, action) => {
            state.periodFilter = action.payload;
        },
        setSelectedMonth: (state, action) => {
            state.selectedMonth = action.payload;
        },
        setExecutiveOverview: (state, action) => {
            state.executiveOverview = {
                ...state.executiveOverview,
                ...action.payload,
                isSuccess: true,
                isLoading: false,
                noResult: false,
            };
        },
        setMonthlyUsageData: (state, action) => {
            state.monthlyUsageData = {
                ...action.payload,
                isSuccess: true,
                isLoading: false,
                noResult: false,
            };
        },
        setCreditsUsedVsReimbursedData: (state, action) => {
            state.creditsUsedVsReimbursedData = {
                ...action.payload,
                isSuccess: true,
                isLoading: false,
                noResult: false,
            };
        },
        setPrintedVsRequestedData: (state, action) => {
            state.printedVsRequestedData = {
                ...action.payload,
                isSuccess: true,
                isLoading: false,
                noResult: false,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Opening Balance
            .addCase(getOpeningBalance.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getOpeningBalance.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.openingBalance = data?.count ?? data?.value ?? 0;
                state.executiveOverview.openingBalanceChange = data?.change || 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getOpeningBalance.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Allocated Credits
            .addCase(getAllocatedCredits.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getAllocatedCredits.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.allocatedCredits = data?.count ?? data?.value ?? 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getAllocatedCredits.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Credits Used
            .addCase(getCreditsUsed.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getCreditsUsed.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.creditsUsed = data?.count ?? data?.value ?? 0;
                state.executiveOverview.creditsUsedChange = data?.change || 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getCreditsUsed.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Credits Reimbursed
            .addCase(getCreditsReimbursed.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getCreditsReimbursed.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.creditsReimbursed = data?.count ?? data?.value ?? 0;
                state.executiveOverview.creditsReimbursedChange = data?.change || 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getCreditsReimbursed.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Closing Balance
            .addCase(getClosingBalance.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getClosingBalance.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.closingBalance = data?.count ?? data?.value ?? 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getClosingBalance.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Eligible Rollover
            .addCase(getEligibleRollover.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getEligibleRollover.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.eligibleRollover = data?.count ?? data?.value ?? 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getEligibleRollover.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Total Print Jobs
            .addCase(getTotalPrintJobs.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getTotalPrintJobs.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.totalPrintJobs = data?.count ?? data?.value ?? 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getTotalPrintJobs.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Utilization Rate
            .addCase(getUtilizationRate.pending, (state) => {
                state.executiveOverview.isLoading = true;
            })
            .addCase(getUtilizationRate.fulfilled, (state, action) => {
                const data = action.payload?.data;
                state.executiveOverview.utilizationRate = data?.count ?? data?.value ?? 0;
                state.executiveOverview.isLoading = false;
                state.executiveOverview.isSuccess = true;
            })
            .addCase(getUtilizationRate.rejected, (state) => {
                state.executiveOverview.isLoading = false;
            })
            // Monthly Credit Usage
            .addCase(getMonthlyCreditUsage.pending, (state) => {
                state.monthlyUsageData.isLoading = true;
            })
            .addCase(getMonthlyCreditUsage.fulfilled, (state, action) => {
                const data = action.payload?.data;
                let datasets = [];

                // Handle datasets - could be array of numbers or array of objects
                if (Array.isArray(data?.datasets)) {
                    if (data.datasets.length > 0 && typeof data.datasets[0] === 'number') {
                        // Transform array of numbers to chart format
                        datasets = [{ name: "label.CREDIT_USAGE", data: data.datasets }];
                    } else if (data.datasets.every(d => typeof d === 'object' && d !== null)) {
                        datasets = data.datasets;
                    }
                }

                state.monthlyUsageData = {
                    isLoading: false,
                    isSuccess: true,
                    noResult: !data?.labels?.length || !datasets.length,
                    labels: data?.labels || [],
                    datasets: datasets,
                };
            })
            .addCase(getMonthlyCreditUsage.rejected, (state) => {
                state.monthlyUsageData = {
                    isLoading: false,
                    isSuccess: false,
                    noResult: true,
                    labels: [],
                    datasets: [],
                };
            })
            // Credits Used vs Reimbursed
            .addCase(getCreditsUsedVsReimbursed.pending, (state) => {
                state.creditsUsedVsReimbursedData.isLoading = true;
            })
            .addCase(getCreditsUsedVsReimbursed.fulfilled, (state, action) => {
                const data = action.payload?.data;
                let datasets = [];

                // Handle datasets - could be array of numbers or array of objects
                if (Array.isArray(data?.datasets)) {
                    if (data.datasets.every(d => typeof d === 'object' && d !== null)) {
                        datasets = data.datasets;
                    }
                }
                // Handle separate credits_used and credits_reimbursed arrays
                if (Array.isArray(data?.credits_used) && Array.isArray(data?.credits_reimbursed)) {
                    datasets = [
                        { name: "label.CREDITS_USED", data: data.credits_used },
                        { name: "label.CREDITS_REIMBURSED", data: data.credits_reimbursed },
                    ];
                }

                state.creditsUsedVsReimbursedData = {
                    isLoading: false,
                    isSuccess: true,
                    noResult: !data?.labels?.length || !datasets.length,
                    labels: data?.labels || [],
                    datasets: datasets,
                };
            })
            .addCase(getCreditsUsedVsReimbursed.rejected, (state) => {
                state.creditsUsedVsReimbursedData = {
                    isLoading: false,
                    isSuccess: false,
                    noResult: true,
                    labels: [],
                    datasets: [],
                };
            })
            // Printed vs Requested Quantity
            .addCase(getPrintedVsRequestedQuantity.pending, (state) => {
                state.printedVsRequestedData.isLoading = true;
            })
            .addCase(getPrintedVsRequestedQuantity.fulfilled, (state, action) => {
                const data = action.payload?.data;
                let datasets = [];

                // Handle datasets - could be array of numbers or array of objects
                if (Array.isArray(data?.datasets)) {
                    if (data.datasets.every(d => typeof d === 'object' && d !== null)) {
                        datasets = data.datasets;
                    }
                }
                // Handle separate requested and printed arrays
                if (Array.isArray(data?.requested) && Array.isArray(data?.printed)) {
                    datasets = [
                        { name: "label.REQUESTED_QUANTITY", data: data.requested },
                        { name: "label.PRINTED_QUANTITY", data: data.printed },
                    ];
                }

                state.printedVsRequestedData = {
                    isLoading: false,
                    isSuccess: true,
                    noResult: !data?.labels?.length || !datasets.length,
                    labels: data?.labels || [],
                    datasets: datasets,
                };
            })
            .addCase(getPrintedVsRequestedQuantity.rejected, (state) => {
                state.printedVsRequestedData = {
                    isLoading: false,
                    isSuccess: false,
                    noResult: true,
                    labels: [],
                    datasets: [],
                };
            })
            // QC Impact by Month
            .addCase(getQcImpactByMonth.pending, (state) => {
                state.qcImpactByMonthData.isLoading = true;
            })
            .addCase(getQcImpactByMonth.fulfilled, (state, action) => {
                const data = action.payload?.data || action.payload;
                let datasets = [];

                // Handle datasets - could be array of numbers or array of objects
                if (Array.isArray(data?.datasets)) {
                    if (data.datasets.length > 0 && typeof data.datasets[0] === 'number') {
                        // Transform array of numbers to chart format
                        datasets = [{ name: "label.QC_FAIL_QTY", data: data.datasets }];
                    } else if (data.datasets.every(d => typeof d === 'object' && d !== null)) {
                        datasets = data.datasets;
                    }
                }

                state.qcImpactByMonthData = {
                    isLoading: false,
                    isSuccess: true,
                    noResult: !data?.labels?.length || !datasets.length,
                    labels: data?.labels || [],
                    datasets: datasets,
                };
            })
            .addCase(getQcImpactByMonth.rejected, (state) => {
                state.qcImpactByMonthData = {
                    isLoading: false,
                    isSuccess: false,
                    noResult: true,
                    labels: [],
                    datasets: [],
                };
            })
            // QC Fail by Product (Pie Chart)
            .addCase(getQcFailByProduct.pending, (state) => {
                state.qcFailByProductData.isLoading = true;
            })
            .addCase(getQcFailByProduct.fulfilled, (state, action) => {
                // API Response: { success, code, message, data: { total_quantity, data: [{ product_sku, quantity, percentage }] } }
                const responseData = action.payload?.data;
                const dataArray = responseData?.data || [];

                // Transform array to pieLabels and pieTotal
                const pieLabels = dataArray.map(item => item.product_sku);
                const pieTotal = dataArray.map(item => item.percentage);

                state.qcFailByProductData = {
                    isLoading: false,
                    isSuccess: true,
                    noResult: !pieLabels?.length || !pieTotal?.length,
                    pieLabels: pieLabels,
                    pieTotal: pieTotal,
                };
            })
            .addCase(getQcFailByProduct.rejected, (state) => {
                state.qcFailByProductData = {
                    isLoading: false,
                    isSuccess: false,
                    noResult: true,
                    pieLabels: [],
                    pieTotal: [],
                };
            });
    },
});

export const {
    initReportingCredit,
    setActiveTab,
    setPeriodFilter,
    setSelectedMonth,
    setExecutiveOverview,
    setMonthlyUsageData,
    setCreditsUsedVsReimbursedData,
    setPrintedVsRequestedData,
} = reportingCreditSlice.actions;

export const reportingCreditReducer = reportingCreditSlice.reducer;
